import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import { useScrolled } from "../../hooks/use-scrolled";

// `route: true` means navigate via react-router; otherwise it's an in-page hash.
const LINKS = [
  { href: "#product", label: "Technology" },
  { href: "#control", label: "Control" },
  { href: "#design", label: "Design" },
  { href: "#energy", label: "Energy" },
  { href: "#applications", label: "Applications" },
  { href: "#why", label: "Why ecoHeatwave" },
  { href: "#about", label: "About" },
];

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

function NavBarShell({ scrolled, bar, menu }) {
  const tone = scrolled
    ? "backdrop-blur-xl bg-ecoh-bg/80 border-b border-ecoh-ink/10"
    : "backdrop-blur-md bg-black/25 border-b border-white/10";
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${tone}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        {bar}
      </div>
      {menu}
    </header>
  );
}

function DesktopLink({ link, scrolled, onClick }) {
  const colour = scrolled
    ? "text-ecoh-ink/80"
    : "text-white/95 drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]";
  const cls = `text-[13px] font-medium transition-colors duration-300 hover:text-ecoh-accent ${colour}`;
  const testid = `nav-link-${slug(link.label)}`;
  if (link.route) {
    return (
      <Link to={link.href} className={cls} data-testid={testid}>
        {link.label}
      </Link>
    );
  }
  return (
    <a href={link.href} onClick={onClick} className={cls} data-testid={testid}>
      {link.label}
    </a>
  );
}

function ConsultationLink({ scrolled, onClick }) {
  const colour = scrolled
    ? "text-ecoh-ink"
    : "text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]";
  return (
    <a
      href="#consultation"
      onClick={onClick}
      className={`inline-flex items-center text-[13px] font-medium transition-colors duration-300 hover:text-ecoh-accent ${colour}`}
      data-testid="nav-link-consultation"
    >
      Book consultation →
    </a>
  );
}

function MobileToggle({ scrolled, open, onClick }) {
  const colour = scrolled ? "text-ecoh-ink" : "text-white";
  const bar = scrolled ? "bg-ecoh-ink" : "bg-white";
  return (
    <button
      aria-label="Open menu"
      onClick={onClick}
      className={`md:hidden w-10 h-10 grid place-items-center transition-colors ${colour}`}
      data-testid="nav-mobile-toggle"
    >
      <span className="space-y-1.5">
        <span className={`block w-5 h-px transition-transform ${bar} ${open ? "translate-y-1.5 rotate-45" : ""}`} />
        <span className={`block w-5 h-px transition-opacity ${bar} ${open ? "opacity-0" : ""}`} />
        <span className={`block w-5 h-px transition-transform ${bar} ${open ? "-translate-y-1.5 -rotate-45" : ""}`} />
      </span>
    </button>
  );
}

function MobileLink({ link, onClick }) {
  const cls = "text-base text-ecoh-ink/90";
  const testid = `nav-mobile-link-${slug(link.label)}`;
  if (link.route) {
    return (
      <Link to={link.href} onClick={onClick} className={cls} data-testid={testid}>
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      onClick={(e) => onClick(e, link.href)}
      className={cls}
      data-testid={testid}
    >
      {link.label}
    </a>
  );
}

function MobileMenu({ onLinkClick, onRouteClick }) {
  return (
    <div className="md:hidden border-t border-ecoh-ink/10 bg-ecoh-bg/95 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
        {LINKS.map((l) => (
          <MobileLink
            key={l.href}
            link={l}
            onClick={l.route ? onRouteClick : onLinkClick}
          />
        ))}
        <a
          href="#consultation"
          onClick={(e) => onLinkClick(e, "#consultation")}
          className="text-base text-ecoh-accent"
          data-testid="nav-mobile-link-consultation"
        >
          Book consultation →
        </a>
      </div>
    </div>
  );
}

// In-page hash scroll; off the landing page, route home first.
function useHashNav(onNavigate) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const handleHashClick = (e, href) => {
    e.preventDefault();
    onNavigate();
    if (!isHome) {
      navigate(`/${href}`);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogoClick = (e) => {
    if (!isHome) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { handleHashClick, handleLogoClick };
}

function DesktopNav({ scrolled, onHashClick }) {
  return (
    <nav className="hidden md:flex items-center gap-10">
      {LINKS.map((l) => (
        <DesktopLink
          key={l.href}
          link={l}
          scrolled={scrolled}
          onClick={l.route ? undefined : (e) => onHashClick(e, l.href)}
        />
      ))}
      <ConsultationLink
        scrolled={scrolled}
        onClick={(e) => onHashClick(e, "#consultation")}
      />
    </nav>
  );
}

export default function Nav() {
  const scrolled = useScrolled(12);
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { handleHashClick, handleLogoClick } = useHashNav(close);

  const bar = (
    <>
      <Link to="/" onClick={handleLogoClick} className="flex items-center" data-testid="brand-logo">
        <BrandLogo tone={scrolled ? "dark" : "light"} />
      </Link>
      <DesktopNav scrolled={scrolled} onHashClick={handleHashClick} />
      <MobileToggle scrolled={scrolled} open={open} onClick={() => setOpen((v) => !v)} />
    </>
  );

  const menu = open ? <MobileMenu onLinkClick={handleHashClick} onRouteClick={close} /> : null;

  return <NavBarShell scrolled={scrolled} bar={bar} menu={menu} />;
}
